import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../firebase';
import { addDoc, collection, getDocs, doc, deleteDoc, setDoc } from 'firebase/firestore';
import BloodFlowLogo from "../../Assets/BloodflowLogo.png";
import BloodFlowLogoNL from "../../Assets/BloodflowLogoNoLetters.png";
import { ref, uploadBytes, listAll, getDownloadURL, getMetadata, deleteObject, updateMetadata } from 'firebase/storage';
import Spinner from '../Spinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import DeletePatientPopup from './DeletePatientPopup';
import NewFilePopup from './NewFilePopup';
import DeletePrescriptionPopup from './DeletePrescriptionPopup';
import { Activity, ChartBar, FileText, Users, Plus, LogOut, 
  Menu, X, Settings, Upload, Search, File, Clipboard, Heart } from 'lucide-react';
import PrescriptionResultsViewer from './PrescriptionResultsViewer';
import '../styles/animations.css';

const PrescriptionDashboard = () => {
    const [loadingWindow, setLoadingWindow] = useState(true);
    const [nav, setNav] = useState(false);
    const [patients, setPatients] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedPrescription, setSelectedPrescription] = useState([]);
    const [patientPrescriptions, setPatientPrescriptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showNewFileModal, setShowNewFileModal] = useState(false);
    const [showDeletePrescriptionModal, setShowDeletePrescriptionModal] = useState(false);
    const [showAddPatientForm, setShowAddPatientForm] = useState(false);
    const [newPatientData, setNewPatientData] = useState({
        name: '',
        age: '',
        sex: 'male'
    });
    const [interpretationLoading, setInterpretationLoading] = useState(false);
    const [interpretationResults, setInterpretationResults] = useState(null);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
    const interpretationContainerRef = useRef(null);

    const fetchPatients = async () => {
        try {
            const patientsCollection = collection(db, 'prescriptionPatients');
            const snapshot = await getDocs(patientsCollection);
            const patientsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPatients(patientsList);
            setFilteredPatients(patientsList);
        } catch (error) {
            console.error("Error fetching patients:", error);
        } finally {
            setLoadingWindow(false);
        }
    };

    const fetchPatientPrescriptions = async (patientId) => {
        try {
            const prescriptionsRef = ref(storage, `prescriptions/${patientId}`);
            const prescriptionFiles = await listAll(prescriptionsRef);

            const fetchedPrescriptions = await Promise.all(
                prescriptionFiles.items.map(async (itemRef) => {
                    const fileURL = await getDownloadURL(itemRef);
                    const metadata = await getMetadata(itemRef);   

                    let createdAtDate;
                    if (metadata && metadata.customMetadata && metadata.customMetadata.interpretationDate) {
                        createdAtDate = new Date(metadata.customMetadata.interpretationDate);
                    } else {
                        createdAtDate = new Date(metadata.timeCreated);
                    }
                    
                    const formattedDate = createdAtDate.toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    });
                    
                    // Extract interpretation results if available
                    const interpretation = metadata.customMetadata?.interpretation 
                        ? JSON.parse(metadata.customMetadata.interpretation) 
                        : null;
                    
                    return {
                        name: itemRef.name,
                        url: fileURL,
                        createdAt: createdAtDate,
                        date: formattedDate,
                        interpretation: interpretation
                    };
                })
            );

            const sortedFiles = fetchedPrescriptions.sort((a, b) => b.createdAt - a.createdAt);

            setPatientPrescriptions(sortedFiles.map(file => ({
                name: file.name,
                date: file.date,
                url: file.url,
                interpretation: file.interpretation
            })));
        } catch (error) {
            console.error("Error fetching patient prescriptions:", error);
        }
    };

    useEffect(() => {
        fetchPatients();
    }, []);

    useEffect(() => {
        if (selectedPatient) {
            fetchPatientPrescriptions(selectedPatient.id);
            setInterpretationResults(null);
        }
    }, [selectedPatient]);

    const handleAddPatientClick = () => {
        setShowAddPatientForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPatientData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addPatient = async () => {
        const auth = getAuth();
        const user = auth.currentUser;
    
        if (!user) {
        console.error("User is not authenticated.");
        return;
        }

        if (!newPatientData.name) {
            toast.error("Please fill in the patient name");
            return;
        }
    
        try {
            const newPatient = {
                ...newPatientData,
                createdAt: new Date(),
                createdBy: user.uid,
            };
    
            // Add the patient and get the document reference
            const docRef = await addDoc(collection(db, 'prescriptionPatients'), newPatient);
            
            // Update the patient with its document ID
            await setDoc(doc(db, 'prescriptionPatients', docRef.id), {
                id: docRef.id
            }, { merge: true });

            toast.success("Patient added successfully.");
            setShowAddPatientForm(false);
            setNewPatientData({
                name: '',
                age: '',
                sex: 'male'
            });
            fetchPatients();
        } catch (error) {
            console.error('Error adding patient:', error.message);
            toast.error("Failed to add patient.");
        }
    };

    const handleNewFileClick = () => {
        if (!selectedPatient) {
            toast.error("Please select a patient first");
            return;
        }
        
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf';
        input.multiple = true;
        input.onchange = handleFileChange;
        input.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedPrescription(files);
        setShowNewFileModal(true);
    };

    const uploadPrescription = async (patientId) => {
        try {
            if (selectedPrescription.length === 0) {
                toast.error("No files selected.");
                return;
            }
    
            for (const file of selectedPrescription) {
                const fileRef = ref(storage, `prescriptions/${patientId}/${file.name}`);
                await uploadBytes(fileRef, file);
            }
    
            setShowNewFileModal(false);
            toast.success("Prescription files uploaded successfully.");
            setSelectedPrescription([]);
            fetchPatientPrescriptions(patientId);
        } catch (error) {
            console.error("Error uploading files:", error);
            toast.error("Failed to upload files.");
        }
    };

    const handlePatientSelect = (patient) => {
        setSelectedPatient(patient);
        setInterpretationResults(null);
    };

    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        searchPatientByName(term);
    };

    const searchPatientByName = (term) => {
        if (!term) {
            setFilteredPatients(patients);
            return;
        }
        const searchResults = patients.filter(patient => 
            patient.name.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredPatients(searchResults);
    };

    const handleLogout = async () => {
        try {
            const auth = getAuth();
            await auth.signOut();
            toast.success("You have been logged out.");
            navigate('/');
        } catch (error) {
            toast.error("Error logging out.");
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const confirmDeletePatient = async (patientId) => {
        try {
            const patientDocRef = doc(db, 'prescriptionPatients', patientId);
            await deleteDoc(patientDocRef);
            toast.success("Patient deleted successfully.");
            fetchPatients();
            setSelectedPatient(null);
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting patient:", error);
            toast.error("Error deleting patient.");
        }
    };

    const confirmDeletePrescription = async () => {
        try {
            if (selectedPrescription.length > 0) {
                const prescriptionToDelete = selectedPrescription[0];
                const prescriptionRef = ref(storage, `prescriptions/${selectedPatient.id}/${prescriptionToDelete.name}`);
                await deleteObject(prescriptionRef);
                toast.success("Prescription deleted successfully.");
    
                setPatientPrescriptions((prescriptions) => prescriptions.filter(
                    prescription => prescription.name !== prescriptionToDelete.name
                ));
    
                setShowDeletePrescriptionModal(false);
                setSelectedPrescription([]);
            }
        } catch (error) {
            console.error("Error deleting prescription:", error);
            toast.error("Failed to delete prescription.");
        }
    };

    const handleInterpretClick = async (prescriptionFile) => {
        if (!selectedPatient || !prescriptionFile) {
            toast.error("Please select a patient and a prescription");
            return;
        }

        setInterpretationLoading(true);
        setInterpretationResults(null);

        try {
            const prescriptionFileRef = ref(storage, `prescriptions/${selectedPatient.id}/${prescriptionFile.name}`);
            const fileURL = await getDownloadURL(prescriptionFileRef);
            
            const fileMetadata = await getMetadata(prescriptionFileRef);
            
            // If already interpreted, fetch from metadata
            if (fileMetadata.customMetadata?.interpretation) {
                const interpretation = JSON.parse(fileMetadata.customMetadata.interpretation);
                setInterpretationResults(interpretation);
                
                if (interpretationContainerRef.current) {
                    setTimeout(() => {
                        interpretationContainerRef.current.scrollIntoView({ behavior: 'smooth' });
                    }, 500);
                }
                setInterpretationLoading(false);
                return;
            }
            
            // Call the API to interpret the prescription
            const interpretationResponse = await fetch(`https://api.bloodflow.eu/api/v1/prescription/interpret`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pdfUrl: fileURL }),
            });
            
            if (!interpretationResponse.ok) {
                throw new Error('Failed to interpret prescription');
            }
            
            const interpretationData = await interpretationResponse.json();
            setInterpretationResults(interpretationData.data);
            
            // Save the interpretation results to metadata
            await updateMetadata(prescriptionFileRef, {
                customMetadata: {
                    ...fileMetadata.customMetadata,
                    interpretation: JSON.stringify(interpretationData.data),
                    interpretationDate: new Date().toISOString(),
                }
            });
            
            // Update patient record with latest prescription info
            await setDoc(doc(db, 'prescriptionPatients', selectedPatient.id), {
                ...selectedPatient,
                lastPrescription: {
                    date: new Date(),
                    fileName: prescriptionFile.name,
                    healthcareInstitution: interpretationData.data.healthcareInstitution,
                    doctor: interpretationData.data.doctor?.name || "Unknown"
                }
            }, { merge: true });
            
            if (interpretationContainerRef.current) {
                setTimeout(() => {
                    interpretationContainerRef.current.scrollIntoView({ behavior: 'smooth' });
                }, 500);
            }
            
        } catch (error) {
            console.error("Error interpreting prescription:", error);
            toast.error("Failed to interpret prescription");
        } finally {
            setInterpretationLoading(false);
        }
    };

    const handleViewPdf = (fileUrl) => {
        setSelectedPdfUrl(fileUrl);
        setShowPdfViewer(true);
    };

    const handleClosePdfViewer = () => {
        setShowPdfViewer(false);
        setSelectedPdfUrl('');
    };

    const handleSideBar = () => {
        setNav(!nav);
    };

    return (
        loadingWindow ? (
            <Spinner />
        ) : (
            <div className="flex h-screen overflow-hidden">
                <aside className={`
                    fixed inset-y-0 left-0 z-50 
                    w-[280px] bg-white border-r border-gray-100
                    transform transition-transform duration-300 ease-in-out
                    ${nav ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                `}>
                    <div className="flex flex-col h-full">
                        <div className="p-4 -mb-4">
                            <button 
                                onClick={handleSideBar}
                                className="md:hidden px-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
    
                        <div className="p-2 border-b border-gray-100">
                            <div className="bg-gradient-to-br from-white via-red-50 to-white rounded-xl p-4 relative overflow-hidden">
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>

                                <img className="w-24 mx-auto relative" src={BloodFlowLogo} alt="BloodFlow Logo" />
                                <h3 className="text-center text-gray-600 text-md relative">Prescription Portal</h3>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="space-y-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                    <input 
                                        type="text" 
                                        value={searchTerm} 
                                        onChange={handleSearchChange} 
                                        placeholder="Search for a patient..." 
                                        className="pl-10 p-2.5 border rounded-lg w-full text-sm"
                                    />
                                </div>

                                <div className="flex items-center justify-between p-2">
                                    <div className="flex items-center">
                                        <Users className="w-4 h-4 text-gray-400" />
                                        <span className="ml-2 text-sm font-medium text-gray-600">
                                            Patients
                                        </span>
                                    </div>
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {filteredPatients.length}
                                    </span>
                                </div>
    
                                <div className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto">
                                    {filteredPatients.length > 0 ? (
                                        filteredPatients.map((patient, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handlePatientSelect(patient)}
                                                className={`
                                                    w-full text-left px-3 py-2 rounded-lg text-sm
                                                    ${selectedPatient?.id === patient.id 
                                                        ? 'bg-red-50 text-red-700 font-medium' 
                                                        : 'text-gray-600 hover:bg-gray-50'
                                                    }
                                                `}
                                            >
                                                {patient.name}
                                            </button>
                                        ))
                                    ) : (
                                        <div className="text-center py-6">
                                            <div className="inline-flex p-3 rounded-full bg-red-50 mb-3">
                                                <Users className="w-6 h-6 text-red-500" />
                                            </div>
                                            <p className="text-sm text-gray-500">No patients found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
    
                        <div className="p-4 border-t border-gray-100">
                            <div className="space-y-2">
                                <button
                                    onClick={handleAddPatientClick}
                                    className="w-full flex items-center justify-center px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    <span className="text-sm font-medium">Add New Patient</span>
                                </button>
    
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center justify-center px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    <span className="text-sm font-medium">Logout</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </aside>
    
                <main className="flex-1 md:ml-[280px] flex flex-col h-screen w-screen px-0 -ml-3 md:px-4">
                    <header className="stagger-animation stagger-1 sticky top-0 z-40 bg-white border-b border-gray-100">
                        <div className="flex h-16 items-center justify-between px-4">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={handleSideBar}
                                    className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <Menu className="h-6 w-6 text-gray-500" />
                                </button>
                                <h1 className="text-xl font-semibold text-gray-900">
                                    Prescription Dashboard
                                    <h1 className="text-sm text-black font-light">
                                        Manage and interpret medical prescriptions
                                    </h1>
                                </h1>
                            </div>
    
                            <div className="flex items-center space-x-4">
                                <button 
                                    onClick={handleNewFileClick}
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <Upload className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                        </div>
                    </header>
    
                    <div className="flex-1 overflow-y-auto bg-gray-50">
                        {showAddPatientForm && (
                            <div className="stagger-animation stagger-2 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden rounded-lg mx-4 my-6">
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>

                                <div className="relative">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2.5 bg-red-50 rounded-xl">
                                            <Users className="w-6 h-6 text-red-500" />
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-900">Add New Patient</h2>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={newPatientData.name}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                                            <input
                                                type="number"
                                                name="age"
                                                value={newPatientData.age}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Sex</label>
                                            <select
                                                name="sex"
                                                value={newPatientData.sex}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                            >
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex justify-end mt-8 space-x-3">
                                        <button 
                                            onClick={() => setShowAddPatientForm(false)}
                                            className="px-6 py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={addPatient}
                                            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm"
                                        >
                                            Add Patient
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="stagger-animation stagger-2 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden mx-4 my-6">
                            <div className="absolute inset-0">
                                <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                            </div>

                            <div className="relative">
                                {selectedPatient ? (
                                    <div className="flex flex-col md:flex-row gap-6">
                                        <div className="w-full md:w-2/3">
                                            <div className="h-full bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl border border-gray-100 hover:border-red-100">
                                                <div className="flex items-center justify-between mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-16 w-16 rounded-xl bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center ring-2 ring-white shadow-lg">
                                                            <span className="text-xl font-bold text-white">
                                                                {selectedPatient.name[0]}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-xl font-bold text-gray-900">{selectedPatient.name}</h3>
                                                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                                                {selectedPatient.age && <span>{selectedPatient.age} years</span>}
                                                                {selectedPatient.age && <span className="text-gray-400">•</span>}
                                                                <span>{selectedPatient.sex}</span>
                                                                {selectedPatient.id && (
                                                                    <>
                                                                        <span className="text-gray-400">•</span>
                                                                        <span>ID: {selectedPatient.id}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600">
                                                        Patient
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-1 gap-6 mt-8">
                                                    {selectedPatient.medicalHistory && (
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="p-1.5 bg-red-50 rounded-lg">
                                                                    <Heart className="w-4 h-4 text-red-500" />
                                                                </div>
                                                                <span className="text-sm font-medium text-gray-700">Medical History</span>
                                                            </div>
                                                            <p className="text-sm text-gray-600">
                                                                {selectedPatient.medicalHistory}
                                                            </p>
                                                        </div>
                                                    )}
                                                    
                                                    {selectedPatient.notes && (
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="p-1.5 bg-red-50 rounded-lg">
                                                                    <FileText className="w-4 h-4 text-red-500" />
                                                                </div>
                                                                <span className="text-sm font-medium text-gray-700">Notes</span>
                                                            </div>
                                                            <p className="text-sm text-gray-600">
                                                                {selectedPatient.notes}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {selectedPatient.lastPrescription && (
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="p-1.5 bg-red-50 rounded-lg">
                                                                    <Clipboard className="w-4 h-4 text-red-500" />
                                                                </div>
                                                                <span className="text-sm font-medium text-gray-700">Last Prescription</span>
                                                            </div>
                                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium">Date: </span>
                                                                    {new Date(selectedPatient.lastPrescription.date.seconds * 1000).toLocaleDateString()}
                                                                </p>
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium">Doctor: </span>
                                                                    {selectedPatient.lastPrescription.doctor}
                                                                </p>
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium">Institution: </span>
                                                                    {selectedPatient.lastPrescription.healthcareInstitution || "Unknown"}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="w-full md:w-1/3">
                                            <div className="bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl border border-gray-100 hover:border-red-100 h-full flex flex-col">
                                                <div className="flex items-center gap-3 mb-6">
                                                    <div className="p-2.5 bg-red-50 rounded-xl">
                                                        <Settings className="w-5 h-5 text-red-500" />
                                                    </div>
                                                    <span className="text-lg font-bold text-gray-900">Patient Management</span>
                                                </div>

                                                <div className="flex-1 flex flex-col justify-center space-y-4">
                                                    <button 
                                                        onClick={() => handleNewFileClick()} 
                                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-sm transform hover:-translate-y-0.5"
                                                    >
                                                        <Upload className="w-4 h-4" />
                                                        <span className="text-sm font-medium">Upload Prescription</span>
                                                    </button>

                                                    <button 
                                                        onClick={handleDeleteClick} 
                                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-800 text-white hover:bg-black transition-all duration-300 shadow-sm transform hover:-translate-y-0.5"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        <span className="text-sm font-medium">Delete Patient</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-6 p-8 text-center">
                                        <div className="relative">
                                            <div className="absolute inset-0 bg-red-100 rounded-full blur-xl animate-pulse"></div>
                                            <div className="relative bg-white p-4 rounded-xl shadow-sm border border-red-100">
                                                <Clipboard className="w-8 h-8 text-red-500" />
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                                No Patient Selected
                                            </h2>
                                            <p className="text-gray-600">Select a patient from the sidebar or add a new one</p>
                                        </div>
                                        
                                        <button
                                            onClick={handleAddPatientClick}
                                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm"
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span>Add New Patient</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedPatient && patientPrescriptions.length > 0 && (
                            <div className="stagger-animation stagger-3 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden mx-4 my-6">
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>

                                <div className="relative">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2.5 bg-red-50 rounded-xl">
                                            <File className="w-5 h-5 text-red-500" />
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-900">Prescription Documents</h2>
                                    </div>
                                    
                                    <div className="bg-gray-50 p-6 rounded-xl">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {patientPrescriptions.map((prescription, index) => (
                                                <div key={index} className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="p-1.5 bg-red-50 rounded-md">
                                                                <File className="w-4 h-4 text-red-500" />
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-700 truncate max-w-[140px]">
                                                                {prescription.name}
                                                            </span>
                                                        </div>
                                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                                            {prescription.date}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="space-y-3">
                                                        {prescription.interpretation ? (
                                                            <>
                                                                <div className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full inline-flex items-center">
                                                                    <FileText className="w-3 h-3 mr-1" />
                                                                    <span>Interpreted</span>
                                                                </div>
                                                                
                                                                <div className="pt-2 space-y-2">
                                                                    <p className="text-xs text-gray-500">
                                                                        <span className="font-medium">Institution: </span>
                                                                        {prescription.interpretation.healthcareInstitution || "Unknown"}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        <span className="font-medium">Doctor: </span>
                                                                        {prescription.interpretation.doctor?.name || "Unknown"}
                                                                    </p>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full inline-flex items-center">
                                                                <Activity className="w-3 h-3 mr-1" />
                                                                <span>Not interpreted</span>
                                                            </div>
                                                        )}
                                                        
                                                        <div className="flex items-center gap-2 pt-3">
                                                            <button
                                                                onClick={() => handleViewPdf(prescription.url)}
                                                                className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition-all duration-200"
                                                            >
                                                                View PDF
                                                            </button>
                                                            <button
                                                                onClick={() => handleInterpretClick(prescription)}
                                                                className="flex-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium transition-all duration-200"
                                                            >
                                                                Interpret
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedPrescription([prescription]);
                                                                    setShowDeletePrescriptionModal(true);
                                                                }}
                                                                className="p-2 bg-gray-100 hover:bg-gray-200 rounded"
                                                            >
                                                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {interpretationResults && !interpretationLoading && (
                            <div ref={interpretationContainerRef} className="stagger-animation stagger-4 mx-4 my-6">
                                <PrescriptionResultsViewer 
                                    results={interpretationResults}
                                    pdfUrl={selectedPdfUrl}
                                    onBack={() => {
                                        setInterpretationResults(null);
                                        setSelectedPrescription([]);
                                    }}
                                />
                            </div>
                        )}

                        {interpretationLoading && (
                            <div ref={interpretationContainerRef} className="stagger-animation stagger-4 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden mx-4 my-6">
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>
                                <div className="relative">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 bg-red-50 rounded-xl">
                                                <ChartBar className="w-5 h-5 text-red-500" />
                                            </div>
                                            <h2 className="text-xl font-bold text-gray-900">Prescription Interpretation Results</h2>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                                        <p className="text-lg font-medium text-gray-600">Interpreting prescription...</p>
                                        <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <footer className="relative stagger-animation stagger-5 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden mx-4 my-6">
                            <div className="absolute inset-0 overflow-hidden">
                                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-[500px] h-[200px]"></div>
                                <div className="absolute inset-0 bg-gradient-to-b from-red-50/50 to-transparent blur-3xl"></div>
                            </div>

                            <div className="relative">
                                <div className="flex justify-center">
                                    <div className="relative group">
                                        <div className="absolute inset-0 bg-red-100 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                                        <img 
                                            className="w-32 md:w-40 relative transform hover:scale-105 transition-transform duration-300" 
                                            src={BloodFlowLogoNL} 
                                            alt="BloodFlow Logo" 
                                        />
                                    </div>
                                </div>

                                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                                    <div className="flex flex-col items-center gap-4">
                                        <p className="text-center text-sm text-gray-600 font-medium">
                                            © {new Date().getFullYear()} BloodFlow. All Rights Reserved
                                        </p>

                                        <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-red-200 to-transparent"></div>

                                        <p className="text-center text-xs text-gray-500">
                                            Powering healthcare with advanced AI analytics
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </footer>
                    </div>
                </main>

                {nav && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                        onClick={handleSideBar}
                    />
                )}
    
                {showNewFileModal && (
                    <NewFilePopup
                        show={showNewFileModal}
                        onClose={() => {
                            setShowNewFileModal(false);
                            setSelectedPrescription([]);
                        }}
                        onConfirm={() => uploadPrescription(selectedPatient.id)}
                        fileName={selectedPrescription.map(file => file.name || file)}
                    />
                )}
                
                {showDeleteModal && selectedPatient && (
                    <DeletePatientPopup
                        show={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={() => confirmDeletePatient(selectedPatient.id)}
                        patientName={selectedPatient.name}
                    />
                )}
                
                <DeletePrescriptionPopup 
                    show={showDeletePrescriptionModal}
                    onClose={() => {
                        setShowDeletePrescriptionModal(false);
                        setSelectedPrescription([]);
                    }}
                    onConfirm={confirmDeletePrescription}
                    prescriptionName={selectedPrescription.length > 0 ? selectedPrescription[0].name : ''}
                />

                {showPdfViewer && selectedPdfUrl && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                        <div className="relative w-full max-w-6xl h-[90vh] bg-white rounded-lg p-2">
                            <button 
                                onClick={handleClosePdfViewer}
                                className="absolute top-4 right-4 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 transition-colors duration-300 z-10"
                            >
                                <X className="h-6 w-6" />
                            </button>
                            <iframe 
                                src={selectedPdfUrl} 
                                className="w-full h-full rounded" 
                                title="PDF Viewer" 
                            />
                        </div>
                    </div>
                )}
            </div>)
    );
};

export default PrescriptionDashboard;